import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NewCustomRequisitionModalComponent } from '../../sale/documents/requisitions/modals/custom-requisition/new-custom-requisition-modal/new-custom-requisition-modal.component';
import { ICON } from '@core/util/icon';
import { DEFAULT_REQUISITION_DIMENSIONS } from '../../sale/documents/requisitions/modals';
import { ButtonGroupOption } from 'src/app/shared/atomic/atoms/button-group/button-group.component';
import { RequisitionPrecedent } from '@models/requisitions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BaseComponent } from '@core/base.component';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ConfirmDeleteCustomRequisitionComponent } from '../../sale/documents/requisitions/modals/custom-requisition/confirm-delete-custom-requisition/confirm-delete-custom-requisition.component';
import { EditCustomRequisitionModalComponent } from '../../sale/documents/requisitions/modals/custom-requisition/edit-custom-requisition-modal/edit-custom-requisition-modal.component';
import { Select } from '@ngxs/store';
import { BusinessRequisitionsState } from '@state/business/requisition/business_requisitions.state';
import { Observable } from 'rxjs';
import { TestHooks } from './requisitions-templates.hooks';

enum Filters {
	ALL = 'All',
	ON_TITLE = 'On Title',
	OFF_TITLE = 'Off Title',
}

enum SelectedRequisitionOptions {
	EDIT = 'Edit',
	COPY = 'Copy',
	DELETE = 'Delete',
}

interface VM {
	requisitions: RequisitionPrecedent[];
	selectedRequisition: RequisitionPrecedent;
	selectedRequisitionOptions: DropdownOption<SelectedRequisitionOptions>[];
}

@UntilDestroy()
@Component({
	selector: 'app-requisitions-templates',
	templateUrl: './requisitions-templates.component.html',
	styleUrls: ['./requisitions-templates.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class RequisitionsTemplatesComponent
	extends BaseComponent<VM>
	implements OnInit
{
	testHooks = TestHooks;
	@Select(BusinessRequisitionsState.getAll) private requisitions$: Observable<
		RequisitionPrecedent[]
	>;
	public ICON = ICON;
	public currentFilter = Filters.ALL;
	public filters: ButtonGroupOption<Filters>[] = Object.values(Filters).map(
		(label) => ({
			label: label.concat(' (0)'),
			value: label,
			intercomAttr: label.toLowerCase().replace(' ', '-'),
		}),
	);
	public searchTerm = '';

	constructor(
		private dialog: MatDialog,
		private el: ElementRef,
	) {
		super({
			requisitions: [],
			selectedRequisition: null,
			selectedRequisitionOptions: [],
		});
	}

	ngOnInit(): void {
		this.requisitions$.pipe(untilDestroyed(this)).subscribe((requisitions) => {
			if (!Array.isArray(requisitions)) return;
			this.selectActiveRequisition(requisitions);
			const { allCount, onTitleCount, offTitleCount } = requisitions.reduce(
				(acc, requisition) => {
					acc.allCount++;
					if (requisition.isOnTitle) acc.onTitleCount++;
					else acc.offTitleCount++;
					return acc;
				},
				{ allCount: 0, onTitleCount: 0, offTitleCount: 0 },
			);

			this.filters = this.filters.map((filter) => {
				switch (filter.value) {
					case Filters.ALL:
						filter.label = filter.label.replace(/\d+/, allCount.toString());
						break;
					case Filters.ON_TITLE:
						filter.label = filter.label.replace(/\d+/, onTitleCount.toString());
						break;
					case Filters.OFF_TITLE:
						filter.label = filter.label.replace(
							/\d+/,
							offTitleCount.toString(),
						);
						break;
				}
				return filter;
			});
		});
	}

	openCreateCustomRequisition(data?: { selectedRequisitionId: string }) {
		this.dialog.open(NewCustomRequisitionModalComponent, {
			...DEFAULT_REQUISITION_DIMENSIONS,
			data,
		});
	}

	filterFunction(
		requisition: RequisitionPrecedent,
		searchTerm: string,
		filter: Filters,
	) {
		if (
			searchTerm &&
			!requisition.name.toLowerCase().includes(searchTerm.toLowerCase())
		)
			return false;
		if (filter === Filters.ON_TITLE && !requisition.isOnTitle) return false;
		if (filter === Filters.OFF_TITLE && requisition.isOnTitle) return false;
		return true;
	}

	onSelect(selectedRequisitionPrecedent: RequisitionPrecedent) {
		this.VM = {
			selectedRequisition: selectedRequisitionPrecedent,
			selectedRequisitionOptions: (selectedRequisitionPrecedent.closerDefault
				? [SelectedRequisitionOptions.COPY]
				: Object.values(SelectedRequisitionOptions)
			).map((option) => ({ name: option, value: option })),
		};
	}

	onSelectOption(
		selectedOption: DropdownResult<DropdownOption<SelectedRequisitionOptions>>,
	) {
		switch (selectedOption.result.value) {
			case SelectedRequisitionOptions.EDIT:
				this.dialog.open(EditCustomRequisitionModalComponent, {
					...DEFAULT_REQUISITION_DIMENSIONS,
					data: { selectedRequisitionId: this.VM.selectedRequisition.id },
				});
				break;
			case SelectedRequisitionOptions.COPY:
				this.openCreateCustomRequisition({
					selectedRequisitionId: this.VM.selectedRequisition.id,
				});
				break;
			case SelectedRequisitionOptions.DELETE:
				this.dialog.open(ConfirmDeleteCustomRequisitionComponent, {
					height: 'auto',
					width: '55rem',
					data: { selectedRequisitionId: this.VM.selectedRequisition.id },
				});
				break;
		}
	}

	trackByRequisitionId(_index: number, requisition: RequisitionPrecedent) {
		return requisition.id;
	}

	private selectActiveRequisition(requisitions: RequisitionPrecedent[]) {
		if (requisitions.length === 0) return;
		if (
			!this.VM.selectedRequisition ||
			requisitions.length < this.VM.requisitions.length
		) {
			// On Init, select first element or if element was removed
			this.onSelect(requisitions[0]);
		} else if (requisitions.length > this.VM.requisitions.length) {
			// find and select element that was added
			const addedRequisition = requisitions.find(
				(requisition) =>
					!this.VM.requisitions.map(({ id }) => id).includes(requisition.id),
			);
			this.onSelect(addedRequisition);
		} else {
			// On update, find and select the same element (refreshed data)
			this.onSelect(
				requisitions.find(
					(requisition) => requisition.id === this.VM.selectedRequisition.id,
				) ?? requisitions[0],
			);
		}
		this.VM = {
			requisitions,
		};
		this.el.nativeElement
			?.querySelector('.selected-requisition')
			?.scrollIntoView({ behavior: 'auto', block: 'center' });
	}
}
